import React, { useEffect, useMemo, useState } from "react";
import uuid from "react-uuid";

import moment, { Moment } from "moment";

import {
  isFieldEnrolled,
  useBulkAddUpdateFieldCropPractice,
  useGetAllCarbonProgramOutcomes,
  useGetFarmReportingYear,
  useGetFarms,
  useGetPracticeCrops,
  useTranslation,
  useValidateCropPeriods,
} from "utils";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CropFCPO,
  CropFormValidationError,
  FertilizerManagement,
  FieldListField,
  FieldPracticeResponse,
  PRACTICE_SLUG,
  PracticeBundle,
  PracticeFCPO,
  VIEW_TYPE,
} from "models";

import {
  AlertBanner,
  Button,
  Drawer,
  FieldHeadlineBulk,
  Form,
  HideFCPOFeatureForUnenrolledFarms,
  Skeleton,
  StickyFooterButtons,
  Trans,
  Message as message,
} from "components";

import { PracticeBundleControls } from "pages/app/all-field-view/components/PracticeBundleControls.tsx";
import { BulkEditCropForm } from "pages/app/all-field-view/components/forms/crop/BulkEditCropForm";
import { AddEditOrganicAndMineralFertilizers } from "pages/app/all-field-view/components/forms/fertilizers/AddEditOrganicAndMineralFertilizers.tsx";
import { FertilizerManagementYesNoForm } from "pages/app/all-field-view/components/forms/fertilizers/FertilizerManagementYesNoForm.tsx";
import { AddEditTillageForm } from "pages/app/all-field-view/components/forms/tillage/AddEditTillageForm.tsx";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { getCropYearFromDates } from "pages/app/all-field-view/utils/AllFieldsUtils";
import {
  BulkAddEditCropFormProps,
  RequestWithExistingCrop,
  combineCropsAndPracticeRequest,
  createBulkCropPracticeRequestBody,
  createBulkPracticeRequest,
  createFertilizerManagementRequest,
  getDefaultBulkDataEntryDateValues,
  getFertilizerManagement,
  getPracticeConfigForBulk,
} from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { getCropRecommendedDates, getIsBaselineOrHistoricYear } from "pages/app/all-field-view/utils/dataEntryUtils.ts";
import { InputRequirementConfig, getInputRequirementConfig, shouldShowInput } from "pages/app/all-field-view/utils/getInputRequirementConfig.ts";
import { getBaselineYearsByPracticeType } from "pages/app/field-timeline/utils/PracticeUtils.ts";
import { isDateValidConsideringLastConfirmedCrop } from "pages/app/field-timeline/utils/TimelineUtils.ts";

export type BulkEditCrop = CropFCPO & {
  fieldId: string;
  isMandatory: boolean;
  isBaseline: boolean;
  isPlaceholder?: boolean;
  availablePracticeBundles: PracticeBundle[];
  fertilizerManagement: FertilizerManagement[] | undefined;
  isInRedzone?: boolean;
};

export type BulkEditCropDrawerProps = {
  selectedCrops: BulkEditCrop[];
  selectedTillages: PracticeFCPO[];
  selectedFertilizers: PracticeFCPO[];
  allCrops: {
    [fieldId: string]: FieldPracticeResponse;
  } | null;
  affectedFields: FieldListField[];
  showDrawer: boolean;
  setShowDrawer: (show: boolean) => void;
  onFinish: () => void;
};

const getRequirementConfigForTillages = (inputRequirementConfigPerFieldAndCrop: Array<InputRequirementConfig | null>) =>
  inputRequirementConfigPerFieldAndCrop.reduce<InputRequirementConfig>((acc, config) => {
    if (!config) return acc;

    Object.keys(config).forEach((key) => {
      const currentLevel = acc[key as keyof InputRequirementConfig];
      const newLevel = config[key as keyof InputRequirementConfig];

      if (!newLevel) return;

      if (!currentLevel) {
        acc[key as keyof InputRequirementConfig] = newLevel;
      } else {
        // Priority: requiredForSaving > requiredForProgram > optional
        if (newLevel === "requiredForSaving" || (newLevel === "requiredForProgram" && currentLevel === "optional")) {
          acc[key as keyof InputRequirementConfig] = newLevel;
        }
      }
    });

    return acc;
  }, {});

const getRegionIdForMultipleFields = (affectedFields: FieldListField[]): string | undefined => {
  const regionIds = Array.from(
    new Set(
      affectedFields.map((field) => field.regionId).filter((id) => id !== "" && id != null) // removes empty string, null, undefined
    )
  );

  const allSame = regionIds.every((item, index, array) => item === array[0]);
  if (allSame) return regionIds[0];
  return undefined;
};

const currentYear = moment().year();

const getYear = (cropItem: BulkEditCrop[]): number => {
  console.assert(cropItem[0]?.year, "A crop item should always have a year!"); // eslint-disable-line
  return cropItem[0]?.year!; // eslint-disable-line
};

export const BulkEditCropDrawer = ({
  selectedCrops,
  allCrops,
  affectedFields,
  showDrawer,
  setShowDrawer,
  onFinish,
  selectedTillages,
  selectedFertilizers,
}: BulkEditCropDrawerProps) => {
  // Util Hooks
  const { t } = useTranslation("allFieldView");
  const [form] = Form.useForm<BulkAddEditCropFormProps>();
  const { currentFarm } = useGetFarms();
  const { validatePeriods } = useValidateCropPeriods();

  // API
  const {
    mutateAsync: updateBulk,
    isSuccess,
    isPending,
    isError,
  } = useBulkAddUpdateFieldCropPractice({ fieldIds: affectedFields.map((field) => field.id), farmId: currentFarm?.id });
  const { data: carbonProgramOutcomes, isLoading: isLoadingProgramOutcomes } = useGetAllCarbonProgramOutcomes(currentFarm?.id, currentFarm?.carbonProgram);
  const cropPracticesQuery = useGetPracticeCrops({ regionId: getRegionIdForMultipleFields(affectedFields) });
  const { data: activeFarmReportingYear } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isDataSubmitted } = useSubmissionStatus(activeFarmReportingYear);

  // States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inputRequirementConfigPerFieldAndCrop, setInputRequirementConfigPerFieldAndCrop] = useState<Array<InputRequirementConfig | null>>([]);
  const [inputRequirementConfigForMultipleFields, setInputRequirementConfigForMultipleFields] = useState<InputRequirementConfig | null>(null);
  const [disableTillageAndSeedingInputs, setDisableTillageAndSeedingInputs] = useState<boolean>(false);
  const [disableFertilizerInputs, setDisableFertilizerInputs] = useState<boolean>(false);
  const [cropPeriodValidationErrors, setCropPeriodValidationErrors] = useState<CropFormValidationError[]>([]);
  const [recommendedSeedingDate, setRecommendedSeedingDate] = useState<Moment>(moment());
  const [recommendedHarvestDate, setRecommendedHarvestDate] = useState<Moment>(moment());
  const [optionalInputsAreShown, setOptionalInputsAreShown] = useState<boolean>(false);

  // Practices
  const selectedMineralFertilizers = selectedFertilizers.filter((practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
  const selectedOrganicFertilizers = selectedFertilizers.filter((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);

  const tillagesBulkShow = getPracticeConfigForBulk(selectedTillages, PRACTICE_SLUG.TILLAGES);
  const mineralBulkShow = getPracticeConfigForBulk(selectedMineralFertilizers, PRACTICE_SLUG.MINERAL_FERTILIZERS);
  const organicBulkShow = getPracticeConfigForBulk(selectedOrganicFertilizers, PRACTICE_SLUG.ORGANIC_FERTILIZERS);

  // Requirement configs
  const carbonProgram = currentFarm?.carbonProgram;
  const farmIsEnrolledInCarbonProgram = !!carbonProgram?.id;
  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
  const { cropType } = selectedCrops[0];
  const year = getYear(selectedCrops);
  // Check requirement config to see if there are even possible optional inputs
  const hideOptionalPracticeToggle = !Object.values(inputRequirementConfigForMultipleFields || {}).some((level) => level === "optional");
  const isCurrentYear = year === currentYear;

  // Red Zone
  const isInRedzone = affectedFields.some((field) => field.isInRedzone);

  const getRequirementConfigPerFieldAndSelectedCrop = () => {
    const { requirementConfigPerFieldAndCrop, errors, disableTillageAndSeedingInputs, disableFertilizerInputs } = selectedCrops.reduce(
      (acc, existingCrop) => {
        const field = affectedFields.find((field) => field.id === existingCrop.fieldId);
        if (!field) {
          acc.errors.push({ fieldId: existingCrop.fieldId, cropId: existingCrop.cropId, message: "Could not find field for crop." });
          return acc;
        }
        const carbonProgramResultOfField = carbonProgramOutcomes?.[field.id];

        // RequirementConfig
        const fieldIsEnrolled = isFieldEnrolled(carbonProgramResultOfField);
        const requirementConfig = getInputRequirementConfig(carbonProgram ?? null, carbonProgramResultOfField ?? null, existingCrop, fieldIsEnrolled);

        // Disable Tillage And Seeding Inputs based on availablePracticeBundles
        const baselineYearsByPracticeType = carbonProgramResultOfField ? getBaselineYearsByPracticeType(carbonProgramResultOfField) : {};
        const bundleHasConfirmedData = (bundleSlug: string) =>
          existingCrop.availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.hasConfirmedData;
        const cropYear = Number(existingCrop.year);

        const disableTillageAndSeedingInputs =
          (getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.seedings, cropYear, activeFarmReportingYear, isDataSubmitted) ||
            getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.tillages, cropYear, activeFarmReportingYear, isDataSubmitted)) &&
          !!bundleHasConfirmedData("tillage");

        const disableFertilizerInputs =
          (getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.mineralFertilizers, cropYear, activeFarmReportingYear, isDataSubmitted) ||
            getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.organicFertilizers, cropYear, activeFarmReportingYear, isDataSubmitted)) &&
          !!bundleHasConfirmedData("fertilizer");

        return {
          ...acc,
          requirementConfigPerFieldAndCrop: [...acc.requirementConfigPerFieldAndCrop, requirementConfig],
          disableTillageAndSeedingInputs: acc.disableTillageAndSeedingInputs || disableTillageAndSeedingInputs,
          disableFertilizerInputs: acc.disableFertilizerInputs || disableFertilizerInputs,
        };
      },
      { requirementConfigPerFieldAndCrop: [], errors: [], disableTillageAndSeedingInputs: false, disableFertilizerInputs: false } as {
        requirementConfigPerFieldAndCrop: Array<InputRequirementConfig | null>;
        errors: { fieldId: string; cropId: string; message: string }[];
        disableTillageAndSeedingInputs: boolean;
        disableFertilizerInputs: boolean;
      }
    );

    if (errors.length > 0) {
      message({ content: errors[0].message, icon: "error" });
    }
    setInputRequirementConfigPerFieldAndCrop(requirementConfigPerFieldAndCrop);
    setDisableTillageAndSeedingInputs(disableTillageAndSeedingInputs);
    setDisableFertilizerInputs(disableFertilizerInputs);
  };

  const getCropValidationErrors = (bulkCropRequest: RequestWithExistingCrop[]): CropFormValidationError[] => {
    const cropPeriodValidationErrors: CropFormValidationError[] = [];
    bulkCropRequest.forEach((item) => {
      const { bulkCropPracticesRequest, existingCrop } = item;
      const allCropsByField = allCrops ? allCrops[bulkCropPracticesRequest.fieldId]?.crops : [];
      const { cropId, hasResidue, seedingPracticeType, cropType, seedingStartDate, harvestEndDate } = bulkCropPracticesRequest.crop;
      const seedingDateInMoment = moment(seedingStartDate);
      const harvestDateInMoment = moment(harvestEndDate);
      const cropToValidate: CropFCPO = {
        ...existingCrop,
        seedingStartDate,
        harvestEndDate,
        cropId,
        hasResidue,
        seedingPracticeType: seedingPracticeType || null,
        cropType,
      };

      const cropPeriodValidationErrorList = validatePeriods(cropToValidate, allCropsByField || []);
      cropPeriodValidationErrors.push(...cropPeriodValidationErrorList);

      const isAfterLastConfirmedCrop = isDateValidConsideringLastConfirmedCrop(
        seedingDateInMoment,
        harvestDateInMoment,
        allCropsByField,
        cropToValidate.cropType,
        cropToValidate.year
      );

      if (!cropToValidate.isConfirmed && !isAfterLastConfirmedCrop) {
        cropPeriodValidationErrors.push({
          fieldId: bulkCropPracticesRequest.fieldId,
          message: t("cropForm.errorMessages.notAllowedToMoveBeforeConfirmationDate"),
        });
      }

      const isMainCrop = bulkCropPracticesRequest.crop.cropType === CROP_TYPE_FCPO.MAIN;
      if (isMainCrop) {
        const cropFormFieldTargetYear = harvestDateInMoment.clone().year();

        const hasMainCropForTargetYear = allCropsByField.some(
          (existingCrop) =>
            existingCrop.id !== cropToValidate.id &&
            existingCrop.cropType === CROP_TYPE_FCPO.MAIN &&
            getCropYearFromDates(existingCrop.cropType, moment(existingCrop.seedingStartDate), moment(existingCrop.harvestEndDate)) ===
              cropFormFieldTargetYear &&
            existingCrop.cropId !== null
        );
        if (hasMainCropForTargetYear) {
          cropPeriodValidationErrors.push({
            fieldId: bulkCropPracticesRequest.fieldId,
            message: t("cropForm.errorMessages.notAllowedMultipleMainCropsForSameYear"),
          });
        }
      }
    });

    return cropPeriodValidationErrors;
  };

  const onSubmit = async (cropFormData: BulkAddEditCropFormProps) => {
    setIsLoading(true);
    const touchedFormFields: Partial<BulkAddEditCropFormProps> = form.getFieldsValue({
      filter: (meta) => meta.touched,
    });

    const { data: fertilizerManagementRequestByCropItemId, errors: fertilizerManagementValidationErrors } = createFertilizerManagementRequest({
      formData: cropFormData,
      bulkEditCrops: selectedCrops,
      touchedFormFields: touchedFormFields,
      year: year,
    });

    const { data: tillageRequestByCropItemId, errors: bulkTillageValidationErrors } = createBulkPracticeRequest({
      formData: cropFormData,
      selectedPractices: selectedTillages,
      bulkEditCrops: selectedCrops,
      touchedFormFields,
      bulkPracticeState: tillagesBulkShow?.bulkPracticeState,
      type: PRACTICE_SLUG.TILLAGES,
    });

    const { data: mineralFertilizerRequestByCropItemId, errors: bulkMineralFertilizerValidationErrors } = createBulkPracticeRequest({
      formData: cropFormData,
      selectedPractices: selectedMineralFertilizers,
      bulkEditCrops: selectedCrops,
      touchedFormFields,
      bulkPracticeState: mineralBulkShow?.bulkPracticeState,
      type: PRACTICE_SLUG.MINERAL_FERTILIZERS,
    });

    const { data: organicRequestByCropItemId, errors: bulkOrganicFertilizerValidationErrors } = createBulkPracticeRequest({
      formData: cropFormData,
      selectedPractices: selectedOrganicFertilizers,
      bulkEditCrops: selectedCrops,
      touchedFormFields,
      bulkPracticeState: organicBulkShow?.bulkPracticeState,
      type: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
    });

    const { data, errors } = createBulkCropPracticeRequestBody({
      cropFormData: cropFormData.crop,
      touchedFormFields,
      bulkEditCrops: selectedCrops,
    });

    if (
      errors.length > 0 ||
      bulkTillageValidationErrors.length > 0 ||
      bulkMineralFertilizerValidationErrors.length > 0 ||
      bulkOrganicFertilizerValidationErrors.length > 0 ||
      fertilizerManagementValidationErrors.length > 0
    ) {
      message({ content: errors[0].message, icon: "error" });
      setIsLoading(false);
      return;
    }

    // Validate Dates
    const cropPeriodValidationErrors = getCropValidationErrors(data);

    if (cropPeriodValidationErrors.length) {
      setCropPeriodValidationErrors(cropPeriodValidationErrors);

      setTimeout(() => setCropPeriodValidationErrors([]), 4000);
      setIsLoading(false);
      const firstError = cropPeriodValidationErrors[0];
      message({ content: firstError.message, icon: "error" });
    } else {
      const bulkCropPracticesRequest = data.map((data) => data.bulkCropPracticesRequest);
      // Combine Crop and Tillage request (Fertilizers are not included yet, they will not be sent and ignored in the API)
      const request = combineCropsAndPracticeRequest({
        crops: bulkCropPracticesRequest,
        practices: [...tillageRequestByCropItemId, ...organicRequestByCropItemId, ...mineralFertilizerRequestByCropItemId],
        fertilizerManagements: fertilizerManagementRequestByCropItemId,
      });
      // Need to use the mutateAsync since we need to show the user what crops could not be created/updated in the future
      await updateBulk({ data: request });
    }
    setIsLoading(false);
  };

  const incomplete = () => {
    message({ content: t("errors.required"), icon: "error" });
  };

  const onClose = () => {
    setShowDrawer(false);
  };

  const handleCropSelectionChanged = (cropId: string, year: number, cropType: CROP_TYPE_FCPO) => {
    // Find the selected crop from the practice crops data
    const practiceCrop = cropPracticesQuery.data?.find((crop) => crop.id === cropId);
    if (!practiceCrop) return;

    // Update recommended dates based on the selected crop
    const recommendedDates = getCropRecommendedDates({ crop: practiceCrop, selectedYear: year, cropType });
    if (recommendedDates) {
      setRecommendedSeedingDate(recommendedDates.recommendedSeedingDate);
      setRecommendedHarvestDate(recommendedDates.recommendedHarvestDate);
    }
  };

  useEffect(() => {
    if (isSuccess && !isPending) {
      if (!isError) {
        onFinish();
      }
    }
  }, [isSuccess, isPending, onFinish, isError]);

  useEffect(() => {
    if (carbonProgramOutcomes) {
      getRequirementConfigPerFieldAndSelectedCrop();
    }
  }, [isLoadingProgramOutcomes, carbonProgramOutcomes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const requirementConfigForTillage = getRequirementConfigForTillages(inputRequirementConfigPerFieldAndCrop);
    setInputRequirementConfigForMultipleFields(requirementConfigForTillage);
  }, [inputRequirementConfigPerFieldAndCrop]);

  useEffect(() => {
    const { seedingDate, harvestDate } = getDefaultBulkDataEntryDateValues(selectedCrops);
    setRecommendedSeedingDate(seedingDate);
    setRecommendedHarvestDate(harvestDate);
  }, [selectedCrops]);

  const { hasMixedFertilizerManagement, showFertilizerManagement, fertilizerManagementValue } = useMemo(
    () => getFertilizerManagement(selectedCrops, year, cropType),
    [selectedCrops, year, cropType]
  );

  return (
    <Drawer
      dataTestId="bulk-edit-crop-drawer"
      title={t("allFieldView:titleBulkCropDrawer")}
      visible={showDrawer}
      onClose={onClose}
      destroyOnHidden={true}
      className="add-edit-crop-drawer"
    >
      <FieldHeadlineBulk headline="" affectedFields={affectedFields} selectedCrops={selectedCrops} />
      {cropType !== CROP_TYPE_FCPO.UNDERSOWING && (
        <HideFCPOFeatureForUnenrolledFarms>
          <PracticeBundleControls
            bundles={[]}
            optionalInputsAreShown={optionalInputsAreShown || !farmIsEnrolledInCarbonProgram}
            setOptionalInputsAreShown={setOptionalInputsAreShown}
            isCurrentYear={isCurrentYear}
            hideOptionalPractices={hideOptionalPracticeToggle}
            hidePracticeIcons={isInventoryProgram}
            viewType={VIEW_TYPE.ALL_FIELDS}
          />
        </HideFCPOFeatureForUnenrolledFarms>
      )}
      {/* TODO (Alp): Use "Inline banner" when https://klim-app.atlassian.net/browse/FOOT-1355 is done */}
      <AlertBanner
        className="margin-top-200"
        description={<Trans i18nKey={"allFieldView:cropForm.multiEditInfo"} components={{ strong: <strong /> }} />}
        type="make-aware"
        iconName="tabler/info-circle"
        closeable={true}
        iconAlign="start"
      />
      {isLoadingProgramOutcomes ? (
        <Skeleton type="block" height="15rem" className="margin-top-200" />
      ) : (
        <Form form={form} name="bulkCropForm" onFinish={onSubmit} onFinishFailed={incomplete}>
          <BulkEditCropForm
            selectedItems={selectedCrops}
            inputRequirementConfigPerFieldAndCrop={inputRequirementConfigPerFieldAndCrop}
            optionalInputsAreShown={optionalInputsAreShown || !farmIsEnrolledInCarbonProgram}
            cropPeriodValidationErrors={cropPeriodValidationErrors}
            disableTillageAndSeedingInputs={disableTillageAndSeedingInputs}
            regionId={getRegionIdForMultipleFields(affectedFields)}
            year={year}
            recommendedSeedingDate={recommendedSeedingDate}
            recommendedHarvestDate={recommendedHarvestDate}
            onCropSelectionChanged={handleCropSelectionChanged}
            isInRedzone={isInRedzone}
          />

          {(shouldShowInput(inputRequirementConfigForMultipleFields?.tillages, optionalInputsAreShown) || selectedTillages.length > 0) && (
            <AddEditTillageForm
              cropId={uuid()} // We only need the cropId to create a TILLAGE_BLANK_PRACTICE, the cropId of the TILLAGE_BLANK_PRACTICE is not getting used for anything else
              existingTillages={tillagesBulkShow?.practiceToShow ?? []}
              disabled={disableTillageAndSeedingInputs}
              baselineYears={[]} // Not needed since we can have a mixed values
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              year={year}
              requirementConfig={inputRequirementConfigForMultipleFields}
              optionalInputsAreShown={optionalInputsAreShown || !farmIsEnrolledInCarbonProgram}
              practiceConfigForBulk={tillagesBulkShow ?? undefined}
            />
          )}

          {/* Fertilizer Forms */}
          {(shouldShowInput(inputRequirementConfigForMultipleFields?.fertilizerManagement, optionalInputsAreShown) || showFertilizerManagement) && (
            <FertilizerManagementYesNoForm
              initialValue={fertilizerManagementValue ?? null}
              requirementLevel={inputRequirementConfigForMultipleFields?.fertilizerManagement}
              mixedValues={hasMixedFertilizerManagement}
            />
          )}

          {(shouldShowInput(inputRequirementConfigForMultipleFields?.mineralFertilizers, optionalInputsAreShown) ||
            selectedFertilizers.some(({ type }) => type === PRACTICE_SLUG.MINERAL_FERTILIZERS)) && (
            <AddEditOrganicAndMineralFertilizers
              chosenFertilizerType="mineral"
              disabled={disableFertilizerInputs}
              existingFertilizers={mineralBulkShow?.practiceToShow ?? []}
              baselineYears={[]} // Not needed since we can have a mixed values
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              year={year}
              currency={carbonProgram?.currency}
              requirementLevel={inputRequirementConfigForMultipleFields?.mineralFertilizers}
              practiceConfigForBulk={mineralBulkShow ?? undefined}
            />
          )}
          {(shouldShowInput(inputRequirementConfigForMultipleFields?.organicFertilizers, optionalInputsAreShown) ||
            selectedFertilizers.some(({ type }) => type === PRACTICE_SLUG.ORGANIC_FERTILIZERS)) && (
            <AddEditOrganicAndMineralFertilizers
              chosenFertilizerType="organic"
              disabled={disableFertilizerInputs}
              existingFertilizers={organicBulkShow?.practiceToShow ?? []}
              baselineYears={[]} // Not needed since we can have a mixed values
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              year={year}
              currency={carbonProgram?.currency}
              requirementLevel={inputRequirementConfigForMultipleFields?.mineralFertilizers}
              practiceConfigForBulk={organicBulkShow ?? undefined}
            />
          )}
          <StickyFooterButtons absolute>
            <div className="row-space-between">
              <Button dataTestId="bulk-edit-cancel" onClick={onClose}>
                {t("allFieldView:cancelBulkCropDrawer")}
              </Button>
              <Button dataTestId="bulk-edit-save" loading={isLoading} type="primary" htmlType="submit">
                {t("allFieldView:saveCropDrawer")}
              </Button>
            </div>
          </StickyFooterButtons>
        </Form>
      )}
    </Drawer>
  );
};
